import BulkTemplateDialog from '@extension/context/content/components/bt-bulk-dashboard/bulk-template-dialog'
import { closeBulkDashboard, setActiveBulkView, setActiveTab } from '@common-reducers/CampaignReducer'
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook'
import { useEffect } from 'react'
import BulkAudiencesList from './audiences/bulk-audiences-list'
import BtBulkMangementTabs from './bt-bulk-management-tabs'
import BulkCampaignDialog from './bulk-campaign-dialog'
import CampaignTabHeader from './campaign-tab-header'
import { isExtensionContext } from '@common/services/detect-context'
import CampaignInternalHeader from './campaign-internal-header'
import { useTranslation } from 'react-i18next'
import { namespace } from '@common-config/constants'

interface BTBulkManagementProps {
    /** When true, the legacy in-component tab bar (BtBulkMangementTabs) is not
     *  rendered. Used by the campaigns redesign, which supplies its own
     *  Audiences/Templates/Campaigns tab bar and drives `activeTab` externally,
     *  so the legacy tab row would be a duplicate control. Defaults to false to
     *  preserve behaviour for every existing caller. */
    hideTabs?: boolean;
}

export default function BTBulkManagement({ hideTabs = false }: BTBulkManagementProps = {}) {
    const dispatch = useAppDispatch()
    const activeTab = useAppSelector((state) => state.CampaignReducer.activeTab);
    const { t } = useTranslation(namespace);

    const dispatchCloseBulkDashboard = () => {
        dispatch(closeBulkDashboard());
    }

    useEffect(() => {
        if (activeTab === "audiences"
            || activeTab === "timeAndDate"
            || activeTab === "messageAndFiles"
            || activeTab === "finish"
        ) {
            dispatch(setActiveTab("manageAudiences"))
        }
    }, [activeTab, dispatch])

    // Get the title based on active tab
    const getHeaderTitle = () => {
        switch (activeTab) {
            case 'manageAudiences':
                return t('campaign.manageAudiences');
            case 'manageTemplates':
                return t('campaign.manageTemplates');
            case 'manageCampaigns':
                return t('campaign.manageCampaigns');
            default:
                return t('campaign.title');
        }
    };

    return (
        <div className="flex flex-col h-full w-full direction-ltr">
            {/* hideTabs = embedded in the campaigns redesign, which supplies its
                own "Campaigns" header + close. Suppress this legacy internal
                header there or it overlaps the redesign header (back-button /
                title collision). Standalone bulk dashboard keeps it. */}
            {isExtensionContext() && !hideTabs && (
                <CampaignInternalHeader
                    title={getHeaderTitle()}
                    onBackClick={dispatchCloseBulkDashboard}
                />
            )}
            <div className="flex flex-col items-center w-full flex-1 min-h-0 pt-5">
                {!hideTabs && <BtBulkMangementTabs />}

                {activeTab === 'manageAudiences' && <BulkAudiencesList />}

                {activeTab === 'manageTemplates' && (
                    <div className="flex flex-col items-center w-full max-w-4xl flex-1 min-h-0 px-6">
                        <CampaignTabHeader title={t('campaign.manageTemplates')} subTitle={t('campaign.manageTemplatesSubtitle')} />
                        <div className="flex-1 min-h-0 w-full">
                            <BulkTemplateDialog />
                        </div>
                    </div>
                )}
                {activeTab === 'manageCampaigns' && (
                    <div className="flex flex-col items-center w-full max-w-4xl flex-1 min-h-0 px-6">
                        <CampaignTabHeader title={t('campaign.manageCampaigns')} subTitle={t('campaign.manageCampaignsSubtitle')} />
                        <div className="flex-1 min-h-0 w-full">
                            <BulkCampaignDialog />
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
